/* START Cache Utils =============== */
const setCache = (key: string, data: unknown) => {
  window.localStorage.setItem(key, JSON.stringify(data));
}
const getCache = (key: string) => {
  const val = window.localStorage.getItem(key);
  return val !== null ? JSON.parse(val) : null;
}
/* END Cache Utils =============== */

interface MessageItem {
  id: number;
  title: string;
  message: string;
  cssClass: string;
  ctaText: string;
  ctaLink: string;
  active: boolean;
  timeStart: string;
  timeEnd: string;
  timeCached: number | null;
  type: 'banner' | 'popup';
  hidden: boolean;
}

interface BannerOptions {
  messages: MessageItem[];
  target: string;
}

const bannerMessages = (options: BannerOptions) => {
  const messages = options.messages;
  const target = '.' + options.target;

  const cacheName = 'c_messages';
  const dataTarget = 'messageid';

  if (getCache(cacheName) === null) {
    setCache(cacheName, messages);
  }

  /* Build banner + popup HTML from JSON object data */
  const convertMessageToHTML = (mgsItem: MessageItem) => {
    let html = '';
    const messageClass = mgsItem.type === 'banner' ? 'site-message' : 'site-popup';
    switch(mgsItem.type) {
      case 'banner':
        html = `<div id="${mgsItem.id}" class="${messageClass} alert alert-${mgsItem.cssClass} alert-dismissible fade hidden" data-${dataTarget}="${mgsItem.id}" role="alert"> \
                  <div class="container"> \
                    <div class="banner-wrapper"> \
                      <button type="button" class="close" data-dismiss="alert" aria-label="Close"> \
                        <span aria-hidden="true">&times;</span> \
                      </button> \
                      <h4>${mgsItem.title}</h4> \
                      <p>${mgsItem.message}</p> \
                    </div> \
                  </div> \
                </div>`;
        break;
      case 'popup':
        html = `<div id="${mgsItem.id}" class="${messageClass} alert alert-${mgsItem.cssClass} alert-dismissible fade hidden" data-${dataTarget}="${mgsItem.id}" role="alert"> \
                  <div class="popup-wrapper"> \
                    <div class="flex flex-row justify-between gap-4 mb-8"> \
                      <h3 class="text-lg mb-0">${mgsItem.title}</h3> \
                      <button type="button" class="close" data-dismiss="alert" aria-label="Close"> \
                        <span aria-hidden="true" class="text-2xl">&times;</span> \
                      </button> \
                    </div> \
                    <div class="mb-10">${mgsItem.message}</div> \
                    <div class="flex flex-row gap-4"> \
                      <a target="_blank" href="${mgsItem.ctaLink}" class="primary close" data-dismiss="alert" aria-label="Close"> \
                        <span aria-hidden="true">${mgsItem.ctaText}</span> \
                      </a> \
                      <a type="button" class="secondary close" data-dismiss="alert" aria-label="Close"> \
                        <span aria-hidden="true">I am not interested</span> \
                      </a> \
                    </div> \
                  </div> \
                </div>`;
        break;
    }
    return html;
  }

  /* This function adds banner messages and popups to the DOM */
  const updateMessageDom = (mgs: MessageItem[]) => {
    /* Determine active messages (not hidden and within active time span) */
    const activeMessages = mgs.filter((item) => {
      if (item) {
        const now = new Date().getTime();
        if (!item.hidden && Date.parse(item.timeStart) < now && Date.parse(item.timeEnd) > now) {
          return item.active
        }
      }
    })

    /* Must have .site-messages or "options.target" somewhere in the page to receive banners/popups */
    const targetNode = document.querySelector(target);

    /* Set a 30-second delay to show popups */
    const closeTimer = (id: number) => {
      const popupTimer = setInterval(() => {
        if ((getCache('popup') + 30000) < Date.now()) {
          document.getElementById(String(id))?.classList.remove('hide');
          clearInterval(popupTimer);
        }
      }, 1000)
    }

    if (activeMessages.length > 0) {  // if there are active messages...
      const messageHTML: { id: number; html: string }[] = [];
      activeMessages.forEach((item: MessageItem) => {
        messageHTML.push({ id: item.id, html: convertMessageToHTML(item) });
      });

      /* For each active message... */
      messageHTML.forEach((el) => {
        if (!targetNode) return;
        targetNode.insertAdjacentHTML('beforeend', el.html);
        if (el.html.includes('site-popup')) {
          if (getCache('popup') === null) {
            // Only show popups to 1/4 of visitors
            if (Math.random() > 0.75) {
              setCache('popup', Date.now());
              closeTimer(el.id);
            }
          } else {
            // Wait 30 seconds after they've been on the site before revealing popup
            if ((getCache('popup') + 30000) < Date.now()) {
              document.getElementById(String(el.id))?.classList.remove('hide');
            } else {
              closeTimer(el.id);
            }
          }
        } else {
          // If it's a banner, reveal immediately
          document.getElementById(String(el.id))?.classList.remove('hide');
        }
      });

      if (!targetNode) return;

      /* Setup close button event listener for each popup / banner */
      targetNode.childNodes.forEach((el: ChildNode) => {
        const htmlEl = el as HTMLElement;
        const closeButtons = htmlEl.querySelectorAll?.('.banner-wrapper .close, .popup-wrapper .close');
        closeButtons?.forEach((button) => {
          button.addEventListener('click', (e: Event) => {
            const alert = ((e.currentTarget as HTMLElement).parentElement?.parentElement?.parentElement) ?? null;
            if (!alert) return;
            const id = parseInt(alert.getAttribute(`data-${dataTarget}`) ?? '0');
            const cachedMessages: MessageItem[] = getCache(cacheName);
            /* Make sure messages stay hidden for users who close them */
            cachedMessages.map((v: MessageItem) => {
              if (v.id === id) {
                v.hidden = true;
                v.timeCached = new Date().getTime();
                return v;
              }
              return v;
            });
            // Disable closed message for current client
            setCache(cacheName, cachedMessages);
            // Hide the banner/popup
            alert.classList.add('hide');
          });
        });
      });
    }
  }

  updateMessageDom(getCache(cacheName));
}

/* ==== Define message configuration ========================================= */
const messages: MessageItem[] = [];
messages.push({
  id: 204664,
  title: 'CORONAVIRUS UPDATES',
  message: '<p>Get the most <a href="https://www.cmu.edu/alert/coronavirus/index.html" target="_blank">up-to-date information</a> on Carnegie Mellon\'s response to the coronavirus.</p>',
  cssClass: 'success',
  ctaText: 'Carnegie Mellon\'s response to coronavirus',
  ctaLink: 'https://www.cmu.edu/alert/coronavirus/index.html',
  active: false,
  timeStart: "Mar 11, 2020",
  timeEnd: "Dec 31, 2021",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 299071,
  title: 'We Made Some Changes',
  message: '<p>We updated our website to make it easier for you to find out about our research and development. Just click <a href="/our-work/index.cfm" id="CP___PAGEID=298598,index.cfm,163|">Our Work</a> to find robust insights into what we do.</p>',
  cssClass: 'success',
  ctaText: 'Our Work',
  ctaLink: 'https://www.sei.cmu.edu/our-work/index.cfm',
  active: false,
  timeStart: "Oct 13, 2020",
  timeEnd: "Nov 13, 2020",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 312018,
  title: 'Welcome to our New Blog ',
  message: '<p>The SEI has launched a new and improved SEI Blog that will combine all of our different blogs (SEI, CERT/CC, DevOps, and Insider Threat) into one stream to improve functionality and user experience.</p>',
  cssClass: 'success',
  ctaText: '',
  ctaLink: '',
  active: true,
  timeStart: "Feb 28, 2021",
  timeEnd: "Apr 30, 2021",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 12132022,
  title: 'Interested in helping the SEI improve this site?',
  message: '<p>The SEI is looking to improve our websites navigation to create a better experience for you. If you have 5-10 minutes, we would love for you to take our tree testing study.</p><p><a class="btn btn-sm btn-outline-primary" href="https://ows.io/tj/etyis9zd">Take the Study</a></p>',
  cssClass: 'success',
  ctaText: '',
  ctaLink: '',
  active: true,
  timeStart: "Dec 6, 2022",
  timeEnd: "Dec 13, 2022",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 12132022,
  title: 'Interested in helping the SEI improve this site?',
  message: '<p>The SEI recently completed a research activity to better understand how people navigate our website, and thanks to the efforts of participants like you, we are continuing to improve our websites navigation! Our team is now running another activity to test these improvements, and we would love your help. If you have 5-10 minutes, please take the test. <p><a class="btn btn-sm btn-outline-primary" href="https://ik6yyle0.optimalworkshop.com/treejack/sei-tree-testing-round-2">Click here to take the test</a></p></p>',
  cssClass: 'success',
  ctaText: 'Click here to take the test',
  ctaLink: 'https://ik6yyle0.optimalworkshop.com/treejack/sei-tree-testing-round-2',
  active: true,
  timeStart: "Mar 10, 2023",
  timeEnd: "Mar 19, 2023",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 12132024,
  title: 'Digital Library updates are on the way.',
  message: '<p>In September, we will be updating the Digital Library with more user-friendly URLs and redesigned author pages to include blog posts, technical papers, podcasts, and videos. Check back for updates!</p><p><a class="btn btn-sm btn-outline-primary" href="https://resources.sei.cmu.edu/library/">Go to the Digital Library</a></p>',
  cssClass: 'success',
  ctaText: 'Go to the Digital Library',
  ctaLink: 'https://resources.sei.cmu.edu',
  active: true,
  timeStart: "Sep 19, 2023",
  timeEnd: "Sep 20, 2023",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 12132026,
  title: 'We Made Some Changes',
  message: '<p>We updated our Digital Library with more user-friendly URLs and redesigned author pages to include blog posts, technical papers, podcasts, and videos.</p><p><a class="btn btn-sm btn-outline-primary" href="https://www.sei.cmu.edu/library/">Go to the Digital Library</a></p>',
  cssClass: 'success',
  ctaText: 'Go to the Digital Library',
  ctaLink: 'https://www.sei.cmu.edu',
  active: true,
  timeStart: "Sep 20, 2023 14:00:00",
  timeEnd: "Sep 21, 2023",
  timeCached: null,
  type: 'banner',
  hidden: false
})
messages.push({
  id: 12012023,
  title: 'User Experience (UX) Survey',
  message: '<p>Hello!</p><p>Can you spare <b>10 minutes</b> to help us improve the SEI website? Your answers to this short survey will help us understand <b>how we can make our site more engaging for people like you.</b></p><p>This survey will run from January 10, 2024, until January 18, 2024. Your answers will be kept <b>confidential</b> and used only to improve the website. <b>No personal data</b> or contact information will be collected during the survey.</p><p>If you have any questions about this survey, email us at <a target="_blank" href="mailto:web-feedback@sei.cmu.edu">web-feedback@sei.cmu.edu</a></p>',
  cssClass: 'success',
  ctaText: 'Start survey',
  ctaLink: 'https://sei.az1.qualtrics.com/jfe/form/SV_06uHTI9Frt87TU2',
  active: true,
  timeStart: "Jan 4, 2024 20:00:00",
  timeEnd: "Jan 18, 2024",
  timeCached: null,
  type: 'popup',
  hidden: false
})

// Initiate Banner/Pop-Up Messages
bannerMessages({
  messages: messages,
  target: 'site-messages'
});
